// frontend/src/components/Navbar.js
import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Dropdown } from 'react-bootstrap';
import { Collapse } from 'bootstrap';

function Navbar() {
  const { user, pgUser, signOutUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isLandingPage = location.pathname === '/';
  const isSignInPage = location.pathname === '/login';
  const isSignUpPage = location.pathname === '/signup';
  const navbarCollapseRef = useRef(null);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  // Add this to track window width for debugging
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    const navbarCollapse = document.getElementById('navbarNav');
    if (navbarCollapse) {
      // Initialize collapse with backdrop option
      new Collapse(navbarCollapse, {
        toggle: false
      });

      // Add backdrop click handler
      navbarCollapse.addEventListener('click', (e) => {
        if (e.target === navbarCollapse) {
          const bsCollapse = Collapse.getInstance(navbarCollapse);
          if (bsCollapse) {
            bsCollapse.hide();
          }
        }
      });
    }
  }, []);

  const handleSignOut = async () => {
    await signOutUser();
    navigate('/');
  };

  const handleLinkClick = () => {
    const navbarCollapse = document.getElementById('navbarNav');
    if (navbarCollapse?.classList.contains('show')) {
      const bsCollapse = new Collapse(navbarCollapse);
      bsCollapse.hide();
    }
  };

  const handleToggleClick = () => {
    const navbarCollapse = document.getElementById('navbarNav');
    if (navbarCollapse) {
      const bsCollapse = new Collapse(navbarCollapse);
      if (navbarCollapse.classList.contains('show')) {
        bsCollapse.hide();
      } else {
        bsCollapse.show();
      }
    }
  };

  // Helper function to determine if a nav link is active
  const isActiveLink = (path) => {
    return location.pathname === path;
  };

  // Simplified CustomToggle without any display classes
  const CustomToggle = React.forwardRef(({ onClick }, ref) => (
    <div className="d-flex">
      <div
        ref={ref}
        // onClick={(e) => {
        //   e.preventDefault();
        //   onClick(e);
        // }}
        style={{ cursor: 'pointer' }}
      >
        {(pgUser?.setup_complete !== false && pgUser?.has_access !== false) && (
          <img
            src="/icons/bell-outline.svg"
            alt="Notifications"
            width="30"
            height="30"
            className="nav-icon"
          />
        )}
      </div>
      <div
        ref={ref}
        onClick={(e) => {
          e.preventDefault();
          onClick(e);
        }}
        style={{ cursor: 'pointer', marginLeft: '1rem' }}
      >
        <img
          src="/icons/person-circle.svg"
          alt="User menu"
          width="24"
          height="24"
          className="nav-icon"
        />
      </div>
    </div>
  ));

  return (
    <nav className={`navbar navbar-expand-lg navbar-light ${isLandingPage ? 'landing-navbar' : 'bg-light'}`}>
      <div className="container-fluid" style={{ padding: '0rem' }}>
        {/* Move the toggle button before the logo */}
        {(!isLandingPage || user) && !location.pathname.includes('/login') && !location.pathname.includes('/signup') && (
          <button 
            className="navbar-toggler" 
            type="button" 
            onClick={handleToggleClick}
            aria-controls="navbarNav" 
            aria-expanded="false" 
            aria-label="Toggle navigation"
          >
            <span className="navbar-toggler-icon"></span>
          </button>
        )}
        
        <div className="navbar-brand-container">
          {/* Show logo on all pages */}
          <Link to="/home"><img src="/icons/autochef_logo.svg" alt="" className="company-logo" /></Link>
          {/* Always show text brand, but visibility controlled by d-none d-lg-block */}
          <Link className="navbar-brand d-none d-lg-block" to="/home"><b>AutoChef</b></Link>
        </div>
        
        {/* User dropdown - always visible when user is logged in */}
        {user && (
          <div className="ms-auto d-flex align-items-center">
            {/* Show this on all screen sizes */}
            <Dropdown align="end">
              <Dropdown.Toggle as={CustomToggle} id="user-dropdown" />
              <Dropdown.Menu>
                {/* Hide My Recipes and Preferences when setup_complete !== true and has_access !== true */}
                {(pgUser?.setup_complete === true && pgUser?.has_access === true) && (
                  <>
                    <Dropdown.Item as={Link} to="/recipes/saved" onClick={handleLinkClick} style={{'--bs-dropdown-link-active-bg': '#52cd92'}}>My Recipes</Dropdown.Item>
                    <Dropdown.Item as={Link} to="/preferences" onClick={handleLinkClick} style={{'--bs-dropdown-link-active-bg': '#52cd92'}}>Preferences</Dropdown.Item>
                    <Dropdown.Item as={Link} to="/profile" onClick={handleLinkClick} style={{'--bs-dropdown-link-active-bg': '#52cd92'}}>Profile</Dropdown.Item>
                  </>
                )}
                
                <Dropdown.Divider />
                <Dropdown.Item style={{'--bs-dropdown-link-active-bg': '#52cd92'}} onClick={(e) => { handleLinkClick(); handleSignOut(); }}>Sign Out</Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </div>
        )}

        {/* Auth links that should always be visible */}
        {!user && (
          <div className="ms-auto d-flex landing-auth-links">
            {/* <Link className="nav-link" to="/login">Login</Link> */}
            {/* <Link className="nav-link" to="/signup">Signup</Link> */}
          </div>
        )}

        {/* Collapsible navbar content - hidden on landing page and signin and signup pages */}
        {(!isLandingPage || user) && !location.pathname.includes('/login') && !location.pathname.includes('/signup') && (
          <>
            <div 
              className="collapse navbar-collapse" 
              id="navbarNav"
              ref={navbarCollapseRef}
              data-bs-backdrop="true"
              data-bs-scroll="false"
            >
              <div className="d-flex justify-content-between align-items-center d-lg-none mb-4">
                <h5 className="m-0"></h5>
                <button 
                  className="btn-close" 
                  onClick={handleLinkClick}
                  aria-label="Close navigation"
                />
              </div>
              <ul className="navbar-nav me-auto">
                {user && (
                  <>
                    {/* <li className="nav-item">
                      <Link className="nav-link" to="/pantry" onClick={handleLinkClick}>Pantry</Link>
                    </li> */}
                    {pgUser?.setup_complete && pgUser?.has_access && (
                      <>
                        <li className="nav-item">
                          <Link 
                            className={`nav-link ${isActiveLink('/meal-plan') ? 'active' : ''}`} 
                            to="/meal-plan" 
                            onClick={handleLinkClick}
                          >
                            Meal Plan
                          </Link>
                        </li>
                        <li className="nav-item">
                          <Link 
                            className={`nav-link ${isActiveLink('/grocery-list') ? 'active' : ''}`} 
                            to="/grocery-list" 
                            onClick={handleLinkClick}
                          >
                            Grocery List
                          </Link>
                        </li>
                      </>
                    )}
                    
                    {pgUser?.setup_complete && (
                      <li className="nav-item">
                        <Link 
                          className={`nav-link ${isActiveLink('/recipes') ? 'active' : ''}`} 
                          to="/recipes" 
                          onClick={handleLinkClick}
                        >
                          Explore Recipes
                        </Link>
                      </li>
                    )}
                  </>
                )}
              </ul>
              
              {/* Only show these links in the mobile menu */}
              {user && (
                <ul className="navbar-nav d-lg-none">
                  {/* <li className="nav-item">
                    <Link className="nav-link" to="/profile" onClick={handleLinkClick}>Profile</Link>
                  </li>
                  <li className="nav-item">
                    <Link className="nav-link" to="/preferences" onClick={handleLinkClick}>Preferences</Link>
                  </li> */}
                  <li className="nav-item">
                    <button 
                      className="btn btn-outline-danger" 
                      onClick={(e) => { handleLinkClick(); handleSignOut(); }}
                    >
                      Sign Out
                    </button>
                  </li>
                </ul>
              )}
            </div>
          </>
        )}
        
        {/* Debug info - uncomment to see current width */}
        {/* <div style={{position: 'fixed', bottom: 0, right: 0, background: 'white', padding: '5px', fontSize: '10px'}}>
          Width: {windowWidth}px
        </div> */}
      </div>
    </nav>
  );
}

export default Navbar;
